/**
 * Team Member Authentication Service
 * 
 * Handles authentication for team members (internal users)
 * separate from Auth0 which is used for expert/admin users.
 */

const API_BASE = process.env.REACT_APP_SERVER || 'http://localhost:3030/';

/**
 * Store team member auth data in localStorage
 */
const STORAGE_KEY = 'imaven_team_auth';

/**
 * Check if email belongs to a team member or expert
 */
export async function checkEmailType(email) {
    try {
        const response = await fetch(`${API_BASE}team/auth/check-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        });
        
        if (!response.ok) {
            throw new Error('Failed to check email type');
        }
        
        return await response.json();
    } catch (error) {
        console.error('Check email type error:', error);
        throw error;
    }
}

/**
 * Login as team member
 */
export async function loginTeamMember(email, password) {
    try {
        const response = await fetch(`${API_BASE}team/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Login failed');
        }
        
        if (data.success && data.token) {
            // Store auth data
            saveTeamAuth(data);
        }
        
        return data;
    } catch (error) {
        console.error('Team member login error:', error);
        throw error;
    }
}

/**
 * Accept invitation and set password
 */
export async function acceptInvitation(token, password) {
    try {
        const response = await fetch(`${API_BASE}team/auth/accept-invite`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, password })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to accept invitation');
        }
        
        if (data.success && data.token) {
            // Store auth data
            saveTeamAuth(data);
        }
        
        return data;
    } catch (error) {
        console.error('Accept invitation error:', error);
        throw error;
    }
}

/**
 * Request password reset
 */
export async function forgotPassword(email) {
    try {
        const response = await fetch(`${API_BASE}team/auth/forgot-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to send reset email');
        }
        
        return data;
    } catch (error) {
        console.error('Forgot password error:', error);
        throw error;
    }
}

/**
 * Reset password with token
 */
export async function resetPassword(token, password) {
    try {
        const response = await fetch(`${API_BASE}team/auth/reset-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, password })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to reset password');
        }
        
        return data;
    } catch (error) {
        console.error('Reset password error:', error);
        throw error;
    }
}

/**
 * Change password (when logged in)
 */
export async function changePassword(currentPassword, newPassword) {
    try {
        const token = getTeamToken();
        if (!token) throw new Error('Not authenticated');
        
        const response = await fetch(`${API_BASE}team/auth/change-password`, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ currentPassword, newPassword })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to change password');
        }
        
        return data;
    } catch (error) {
        console.error('Change password error:', error);
        throw error;
    }
}

// ============================================
// LOCAL STORAGE HELPERS
// ============================================

/**
 * Save team auth data to localStorage
 */
function saveTeamAuth(data) {
    const authData = {
        token: data.token,
        user: data.user,
        authenticatedAt: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(authData));
}

/**
 * Get team auth token from localStorage
 */
export function getTeamToken() {
    const authData = getTeamAuth();
    return authData?.token || null;
}

/**
 * Decode a JWT token to get the payload (without verification)
 */
function decodeJWT(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        
        const payload = parts[1];
        // Handle base64url encoding
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const decoded = atob(base64);
        return JSON.parse(decoded);
    } catch (error) {
        console.error('Error decoding JWT:', error);
        return null;
    }
}

/**
 * Check if a JWT token is expired
 */
function isTokenExpired(token) {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) return true; // Assume expired if we can't decode
    
    // exp is in seconds, Date.now() is in milliseconds
    // Add 30 second buffer to handle clock skew
    const expiresAt = decoded.exp * 1000;
    const now = Date.now();
    
    return now >= expiresAt;
}

/**
 * Get team auth data from localStorage
 */
export function getTeamAuth() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return null;
        
        const authData = JSON.parse(stored);
        
        // Check if token is expired - if so, clear it and return null
        if (authData?.token && isTokenExpired(authData.token)) {
            console.warn('Team member token has expired, clearing auth data');
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        
        return authData;
    } catch (error) {
        console.error('Error reading team auth:', error);
        return null;
    }
}

/**
 * Get current team user
 */
export function getTeamUser() {
    const authData = getTeamAuth();
    return authData?.user || null;
}

/**
 * Check if user is authenticated as team member
 */
export function isTeamMemberAuthenticated() {
    return !!getTeamToken();
}

/**
 * Logout team member
 */
export function logoutTeamMember() {
    localStorage.removeItem(STORAGE_KEY);
    // Redirect to login page
    window.location.href = '/login';
}

/**
 * Clear team auth (without redirect)
 */
export function clearTeamAuth() {
    localStorage.removeItem(STORAGE_KEY);
}

// ============================================
// TEAM MANAGEMENT API
// ============================================

/**
 * Get auth headers for team member API calls
 */
export function getTeamAuthHeaders() {
    const token = getTeamToken();
    if (!token) return {};
    return { 'Authorization': `Bearer ${token}` };
}

/**
 * Fetch with team auth
 */
export async function teamFetch(endpoint, options = {}) {
    const token = getTeamToken();
    if (!token) {
        throw new Error('Not authenticated');
    }
    
    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
    };
    
    const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers
    });
    
    // Handle 401/403 - token expired or invalid
    if (response.status === 401 || response.status === 403) {
        clearTeamAuth();
        window.location.href = '/login?expired=true';
        throw new Error('Session expired');
    }
    
    return response;
}

/**
 * Get all team members
 */
export async function getTeamMembers() {
    const response = await teamFetch('team/members');
    const data = await response.json();
    return data.members || [];
}

/**
 * Get a specific team member
 */
export async function getTeamMember(memberId) {
    const response = await teamFetch(`team/members/${memberId}`);
    const data = await response.json();
    return data.member;
}

/**
 * Create (invite) a new team member
 */
export async function createTeamMember(memberData) {
    const response = await teamFetch('team/members', {
        method: 'POST',
        body: JSON.stringify(memberData)
    });
    return await response.json();
}

/**
 * Update a team member
 */
export async function updateTeamMember(memberId, updates) {
    const response = await teamFetch(`team/members/${memberId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
    });
    return await response.json();
}

/**
 * Delete a team member
 */
export async function deleteTeamMember(memberId) {
    const response = await teamFetch(`team/members/${memberId}`, {
        method: 'DELETE'
    });
    return await response.json();
}

/**
 * Resend invitation
 */
export async function resendInvitation(memberId) {
    const response = await teamFetch(`team/members/${memberId}/resend-invite`, {
        method: 'POST'
    });
    return await response.json();
}

/**
 * Get activity log
 */
export async function getActivityLog(options = {}) {
    const params = new URLSearchParams();
    if (options.category) params.append('category', options.category);
    if (options.actorId) params.append('actorId', options.actorId);
    if (options.limit) params.append('limit', options.limit);
    if (options.skip) params.append('skip', options.skip);
    
    const queryString = params.toString();
    const endpoint = `team/activity${queryString ? `?${queryString}` : ''}`;
    
    const response = await teamFetch(endpoint);
    return await response.json();
}

export default {
    checkEmailType,
    loginTeamMember,
    acceptInvitation,
    forgotPassword,
    resetPassword,
    changePassword,
    getTeamToken,
    getTeamAuth,
    getTeamUser,
    isTeamMemberAuthenticated,
    logoutTeamMember,
    clearTeamAuth,
    getTeamAuthHeaders,
    teamFetch,
    getTeamMembers,
    getTeamMember,
    createTeamMember,
    updateTeamMember,
    deleteTeamMember,
    resendInvitation,
    getActivityLog
};
